/**
 * PatientInvoiceCard Component
 *
 * Displays a single invoice with its details and actions (View, Download PDF).
 */

import {
  OpenInNew as OpenInNewIcon,
  PictureAsPdf as PdfIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { Box, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import type { InvoiceResponse } from '@store/generated/payments';

/**
 * Format amount from cents to display string
 */
function formatAmount(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/**
 * Format date string to display format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Get status chip color based on invoice status
 */
function getStatusColor(
  status: string,
): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (status.toLowerCase()) {
    case 'paid':
      return 'success';
    case 'open':
      return 'warning';
    case 'draft':
      return 'default';
    case 'uncollectible':
    case 'void':
      return 'error';
    default:
      return 'info';
  }
}

/**
 * Capitalize the first letter of a string
 */
function capitalizeStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

export interface PatientInvoiceCardProps {
  invoice: InvoiceResponse;
  onView: (invoice: InvoiceResponse) => void;
  onDownload: (invoice: InvoiceResponse) => void;
  /** Which action is currently loading (fetching fresh URL) */
  loadingAction?: 'view' | 'download' | null;
}

/**
 * Individual invoice card displaying invoice info with View and Download actions
 */
export function PatientInvoiceCard({
  invoice,
  onView,
  onDownload,
  loadingAction,
}: PatientInvoiceCardProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        '&:hover': {
          borderColor: 'action.hover',
        },
      }}
    >
      {/* Left: Icon and invoice info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ReceiptIcon sx={{ color: 'text.secondary', fontSize: 24 }} />

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {`Invoice ${invoice.stripe_invoice_id.slice(-8)}`}
            </Typography>
            <Chip
              label={capitalizeStatus(invoice.status)}
              size="small"
              color={getStatusColor(invoice.status)}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDate(invoice.created_at)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              |
            </Typography>
            <Typography variant="caption" fontWeight={500}>
              {formatAmount(invoice.amount_cents)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right: Actions — URLs fetched fresh on click */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => onView(invoice)}
          disabled={loadingAction === 'view'}
          sx={{ color: 'primary.main' }}
          aria-label="View invoice"
        >
          {loadingAction === 'view' ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>

        <IconButton
          size="small"
          onClick={() => onDownload(invoice)}
          disabled={loadingAction === 'download'}
          sx={{ color: 'text.secondary' }}
          aria-label="Download invoice PDF"
        >
          {loadingAction === 'download' ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <PdfIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
}

export default PatientInvoiceCard;
